/**
 * useContactGroups Hook
 *
 * CRUD for contact groups and member management via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  api,
  EmailContactGroup,
  EmailCreateContactGroupInput,
  EmailUpdateContactGroupInput,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

export function useContactGroups(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.email.contactGroups(accountId ?? ''),
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      return api.listEmailContactGroups(accountId);
    },
    enabled: !!accountId,
  });

  const invalidate = useCallback(() => {
    if (accountId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.email.contactGroups(accountId),
      });
    }
  }, [accountId, queryClient]);

  const createGroup = useCallback(
    async (input: EmailCreateContactGroupInput) => {
      const result = await api.createEmailContactGroup(input);
      invalidate();
      return result;
    },
    [invalidate],
  );

  const updateGroup = useCallback(
    async (groupId: string, input: EmailUpdateContactGroupInput) => {
      if (!accountId) return;
      const result = await api.updateEmailContactGroup(groupId, accountId, input);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const deleteGroup = useCallback(
    async (groupId: string) => {
      if (!accountId) return;
      await api.deleteEmailContactGroup(groupId, accountId);
      invalidate();
    },
    [accountId, invalidate],
  );

  const listMembers = useCallback(
    async (groupId: string) => {
      if (!accountId) return { members: [] };
      return api.listEmailContactGroupMembers(groupId, accountId);
    },
    [accountId],
  );

  const addMembers = useCallback(
    async (groupId: string, contactIds: string[]) => {
      if (!accountId) return;
      return api.addEmailContactGroupMembers(groupId, accountId, contactIds);
    },
    [accountId],
  );

  const removeMember = useCallback(
    async (groupId: string, contactId: string) => {
      if (!accountId) return;
      await api.removeEmailContactGroupMember(groupId, contactId, accountId);
    },
    [accountId],
  );

  return {
    groups: queryResult.data?.groups ?? ([] as EmailContactGroup[]),
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
    createGroup,
    updateGroup,
    deleteGroup,
    listMembers,
    addMembers,
    removeMember,
  };
}
